/**
 * SoleadLab 2.0 — Detection ticket (right rail)
 *
 * The single home for everything fault-detection: scan dispatch (moved from
 * the retired floating ScanButton), job status, ranked candidate review with
 * keyboard navigation, batch accept/reject, and Finish review. Rendered
 * inside the right-ticket chrome by EditorPage when rightActiveTicket ===
 * 'detection'.
 *
 * Gating mirrors the retired ScanButton exactly: admin/operator only (UI
 * mirror of soleadlab-api handlers/detections.ts → isAuthorized(['admin',
 * 'operator'])), hidden entirely in share sessions. EditorPage hides the
 * rail icon; the null-return here is defense in depth.
 *
 * Review flow:
 *   - clicking / arrow-keying a row flies the camera to the candidate ring
 *     (mapStore.map.fitBounds with right-ticket-aware padding) and opens the
 *     map ghost popup via mapStore.detectionPopupCandId;
 *   - Enter accepts a ghost through the shared accept core
 *     (utils/detectionAccept.ts) using the scan's indication type — anomaly
 *     ghosts have no type, so Enter routes to the popup's type picker;
 *   - Backspace / R / Delete rejects the active ghost.
 *
 * Batch accept rides projectStore.addIndicationsBulk (ONE optimistic set(),
 * chunked /indications/bulk POSTs, `_version: 1` stamping — see the store).
 */

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useProjectStore, useUIStore, INDICATION_TYPES } from '../../stores';
import type { Indication, IndicationType } from '../../stores';
import { useMapStore } from '../../stores/mapStore';
import { useAuthStore } from '../../stores/authStore';
import { useDetectionStore } from '../../stores/detectionStore';
import type { DetectionCandidate } from '../../stores/detectionStore';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { pickCurrentOrtho } from '../../utils/activeOrtho';
import {
  resolveExemplars,
  EXEMPLARS_MIN,
  type ExemplarTier,
} from '../../utils/detectionExemplars';
import { buildAcceptedIndication } from '../../utils/detectionAccept';
import { acceptGhostAsIndication } from '../../utils/detectionAccept';
import { centroidOfPolygon } from '../../utils/geometry';
import './DetectionPanel.css';

const TIER_HINT: Record<ExemplarTier, string> = {
  layer: 'Exemplars: your marks on this ortho layer',
  date: "Exemplars: your marks from this layer's capture date",
  name: "Exemplars: your marks matching the date in this layer's name",
  site: 'Exemplars: your most recent site marks of this type',
};

// Survives ticket close/reopen (the panel unmounts with the drawer) so the
// error state can offer a real Retry and the spinner can show elapsed time.
let lastDispatch: {
  siteId: string;
  orthoLayerId: string;
  tilesetId: string;
  mode: 'similar' | 'anomaly';
  indicationType?: IndicationType;
  exemplarIds?: string[];
} | null = null;
let scanStartedAt: number | null = null;

function formatElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export default function DetectionPanel() {
  // ── Gating (identical predicate to the retired ScanButton) ──
  const isReadOnly = useIsReadOnlyViewer();
  const authUser = useAuthStore((s) => s.user);
  const currentOrg = useAuthStore((s) => s.currentOrg);
  const canScan =
    authUser?.role === 'admin' ||
    currentOrg?.role === 'owner' ||
    currentOrg?.role === 'admin' ||
    currentOrg?.role === 'operator';

  // ── Stores (individual selectors — React #185 pattern) ──
  const siteId = useProjectStore((s) => s.meta.siteId);
  const orthophotos = useProjectStore((s) => s.data.orthophotos);
  const indications = useProjectStore((s) => s.data.indications);
  const addIndicationsBulk = useProjectStore((s) => s.addIndicationsBulk);
  const activeOrthophotoId = useMapStore((s) => s.activeOrthophotoId);
  const addNotification = useUIStore((s) => s.addNotification);
  const showConfirm = useUIStore((s) => s.showConfirm);

  const job = useDetectionStore((s) => s.job);
  const candidates = useDetectionStore((s) => s.candidates);
  const threshold = useDetectionStore((s) => s.threshold);
  const summary = useDetectionStore((s) => s.summary);
  const setThreshold = useDetectionStore((s) => s.setThreshold);
  const startScan = useDetectionStore((s) => s.startScan);
  const markAccepted = useDetectionStore((s) => s.markAccepted);
  const markRejected = useDetectionStore((s) => s.markRejected);
  const dismiss = useDetectionStore((s) => s.dismiss);
  const clear = useDetectionStore((s) => s.clear);

  const status = job?.status ?? null;
  const busy = status === 'pending' || status === 'running';
  const failed = status === 'error';

  // ── Elapsed clock while scanning ──
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!busy) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [busy]);

  // ── Current ortho + per-type exemplar rows ──
  const ortho = useMemo(
    () => pickCurrentOrtho(orthophotos, activeOrthophotoId),
    [orthophotos, activeOrthophotoId],
  );

  const typeRows = useMemo(() => {
    if (!ortho) return [];
    const rows: Array<{
      type: IndicationType;
      selection: ReturnType<typeof resolveExemplars>;
      syncedCount: number;
    }> = [];
    for (const type of Object.keys(INDICATION_TYPES) as IndicationType[]) {
      if (!INDICATION_TYPES[type].counted) continue;
      const syncedCount = indications.filter(
        (i) => i.type === type && i._version != null,
      ).length;
      if (syncedCount === 0) continue; // no marks at all — don't list
      rows.push({ type, selection: resolveExemplars(indications, type, ortho), syncedCount });
    }
    rows.sort(
      (a, b) =>
        Number(b.selection.eligible) - Number(a.selection.eligible) ||
        b.syncedCount - a.syncedCount,
    );
    return rows;
  }, [indications, ortho]);

  const tilesetId = ortho?.tilesetId ?? null;
  const scanReady = !!siteId && !!ortho && !!tilesetId && !busy;
  const orthoName = ortho ? ortho.fileName || ortho.name : null;

  // ── Scan dispatch (moved verbatim from ScanButton.launch) ──
  const launch = useCallback(
    (mode: 'similar' | 'anomaly', indicationType?: IndicationType, exemplarIds?: string[]) => {
      if (!siteId || !ortho || !ortho.tilesetId) return;
      lastDispatch = {
        siteId,
        orthoLayerId: ortho.id,
        tilesetId: ortho.tilesetId,
        mode,
        indicationType,
        exemplarIds,
      };
      scanStartedAt = Date.now();
      startScan(siteId, ortho.id, ortho.tilesetId, mode, indicationType, exemplarIds).catch(
        (err) => {
          // startScan only touches store state on success — surface the failure
          // (409 scan-in-progress, 400 exemplar sync race, …) as a toast.
          addNotification({
            type: 'error',
            title: 'Scan failed to start',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        },
      );
    },
    [siteId, ortho, startScan, addNotification],
  );

  const handleRetry = useCallback(() => {
    const d = lastDispatch;
    clear();
    if (d && d.siteId === siteId) {
      scanStartedAt = Date.now();
      startScan(d.siteId, d.orthoLayerId, d.tilesetId, d.mode, d.indicationType, d.exemplarIds).catch(
        (err) => {
          addNotification({
            type: 'error',
            title: 'Scan failed to start',
            message: err instanceof Error ? err.message : 'Unknown error',
          });
        },
      );
    }
  }, [siteId, startScan, clear, addNotification]);

  // ── Review derivations ──
  const ranked = useMemo(
    () => [...candidates].sort((a, b) => b.props.score01 - a.props.score01),
    [candidates],
  );
  const visibleGhosts = useMemo(
    () => candidates.filter((c) => c.state === 'ghost' && c.props.score01 >= threshold),
    [candidates, threshold],
  );
  const belowGhosts = useMemo(
    () => candidates.filter((c) => c.state === 'ghost' && c.props.score01 < threshold),
    [candidates, threshold],
  );
  const acceptedCount = useMemo(
    () => candidates.filter((c) => c.state === 'accepted').length,
    [candidates],
  );
  const rejectedCount = useMemo(
    () => candidates.filter((c) => c.state === 'rejected').length,
    [candidates],
  );

  // Scan-level indication type for similar scans (popup's defaultType logic,
  // hoisted to the scan level): summary verbatim, else any exemplar's type.
  const scanType = useMemo((): IndicationType | '' => {
    if (job?.scanMode !== 'similar') return '';
    const st = (summary as Record<string, unknown> | null)?.indicationType;
    if (typeof st === 'string' && st in INDICATION_TYPES) return st as IndicationType;
    for (const c of candidates) {
      if (!c.props.exemplarId) continue;
      const ex = indications.find((i) => i.id === c.props.exemplarId);
      if (ex) return ex.type;
    }
    return '';
  }, [job?.scanMode, summary, candidates, indications]);

  // ── Camera + popup focus ──
  const focusCandidate = useCallback((cand: DetectionCandidate) => {
    const ms = useMapStore.getState();
    const map = ms.map;
    let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
    for (const [lng, lat] of cand.ring) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    const center = centroidOfPolygon(cand.ring);
    if (map && Number.isFinite(minLng)) {
      try {
        // The right ticket (280px) + rail (72px) overlay the map's right
        // edge — bias the padding so the ring centers in the VISIBLE area.
        const wide = map.getContainer().clientWidth > 900;
        map.fitBounds(
          [[minLng, minLat], [maxLng, maxLat]],
          {
            padding: wide
              ? { top: 120, bottom: 120, left: 120, right: 420 }
              : 48,
            maxZoom: 21,
            duration: 700,
          },
        );
      } catch {
        if (center) ms.flyTo(center, 20); // padding > canvas — small window
      }
    } else if (center) {
      ms.flyTo(center, 20);
    }
    // Open the ghost popup only when it will actually render (visible ghost);
    // MapCanvas auto-clears the id when the candidate leaves the ghost set.
    if (
      cand.state === 'ghost' &&
      cand.props.score01 >= useDetectionStore.getState().threshold
    ) {
      ms.setDetectionPopupCand(cand.id);
    } else {
      ms.setDetectionPopupCand(null);
    }
  }, []);

  // ── Single accept (Enter) via the shared accept core ──
  const acceptOne = useCallback(
    (cand: DetectionCandidate) => {
      if (cand.state !== 'ghost') return;
      const exemplar = cand.props.exemplarId
        ? indications.find((i) => i.id === cand.props.exemplarId)
        : undefined;
      const type = exemplar?.type || scanType;
      if (!type) {
        // Anomaly ghosts need an explicit type — the map popup owns that.
        focusCandidate(cand);
        return;
      }
      const id = acceptGhostAsIndication(cand, type);
      if (!id) return;
      markAccepted(cand.id, id);
      addNotification({
        type: 'success',
        title: `${INDICATION_TYPES[type].label} added · ${Math.round(cand.props.score01 * 100)}% match`,
        duration: 2000,
      });
    },
    [indications, scanType, focusCandidate, markAccepted, addNotification],
  );

  // ── Keyboard navigation within the ranked list ──
  const [activeIdx, setActiveIdx] = useState(0);
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([]);
  useEffect(() => {
    if (activeIdx >= ranked.length) setActiveIdx(Math.max(0, ranked.length - 1));
  }, [ranked.length, activeIdx]);

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (ranked.length === 0) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return; // never swallow browser combos (Ctrl+R, …)
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next =
        e.key === 'ArrowDown'
          ? Math.min(ranked.length - 1, activeIdx + 1)
          : Math.max(0, activeIdx - 1);
      setActiveIdx(next);
      rowRefs.current[next]?.scrollIntoView({ block: 'nearest' });
      focusCandidate(ranked[next]);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cand = ranked[activeIdx];
      if (cand) acceptOne(cand);
    } else if (e.key === 'Backspace' || e.key === 'Delete' || e.key.toLowerCase() === 'r') {
      e.preventDefault();
      const cand = ranked[activeIdx];
      if (cand?.state === 'ghost') markRejected(cand.id);
    }
  };

  // ── Batch actions ──
  const [batchBusy, setBatchBusy] = useState(false);

  const runAcceptAllVisible = useCallback(async () => {
    const j = useDetectionStore.getState().job;
    if (!j || !scanType) return;
    const ghosts = useDetectionStore
      .getState()
      .candidates.filter(
        (c) => c.state === 'ghost' && c.props.score01 >= useDetectionStore.getState().threshold,
      );
    // Build every indication through the SAME accept core as a single
    // accept: geometry = the ghost ring FIRST, topology derived from that
    // real ring (the old build-at-centroid-then-override left fields
    // describing a location the mark no longer occupied). orthoLayerId =
    // the SCANNED layer (the visible layer may have changed mid-review).
    const pairs: Array<{ candId: string; ind: Indication }> = [];
    for (const g of ghosts) {
      const built = buildAcceptedIndication(g.ring, scanType, {
        rotationDeg: g.props.rotationDeg,
        orthoLayerId: j.orthoLayerId,
      });
      if (!built) continue;
      pairs.push({ candId: g.id, ind: built });
    }
    if (pairs.length === 0) return;
    setBatchBusy(true);
    try {
      const res = await addIndicationsBulk(pairs.map((p) => p.ind));
      const created = new Set(res.createdIds);
      let accepted = 0;
      for (const p of pairs) {
        if (created.has(p.ind.id)) {
          markAccepted(p.candId, p.ind.id);
          accepted++;
        }
      }
      useProjectStore.getState().updateAvailableDates();
      const failedCount = res.conflictIds.length + res.failedIds.length;
      addNotification({
        type: failedCount ? 'warning' : accepted ? 'success' : 'info',
        title: `${accepted} ${INDICATION_TYPES[scanType].label} indication${accepted === 1 ? '' : 's'} created`,
        message: failedCount
          ? `${failedCount} not saved — left as ghost suggestions, accept again to retry`
          : undefined,
      });
    } finally {
      setBatchBusy(false);
    }
  }, [scanType, addIndicationsBulk, markAccepted, addNotification]);

  const confirmAcceptAll = () => {
    if (!scanType) return;
    showConfirm({
      title: 'Accept all visible suggestions?',
      message: `Creates ${visibleGhosts.length} ${INDICATION_TYPES[scanType].label} indication${visibleGhosts.length === 1 ? '' : 's'} from the ghost suggestions at or above the sensitivity slider.`,
      confirmLabel: 'Accept all',
      onConfirm: () => {
        void runAcceptAllVisible();
      },
    });
  };

  const confirmRejectBelow = () => {
    const n = belowGhosts.length;
    showConfirm({
      title: 'Reject all below slider?',
      message: `Rejects ${n} ghost suggestion${n === 1 ? '' : 's'} scoring below ${Math.round(threshold * 100)}%. They stay in the list and can be re-reviewed until you finish.`,
      confirmLabel: 'Reject all',
      variant: 'danger',
      onConfirm: () => {
        // Snapshot from the store at confirm time (the slider may have moved).
        const ghosts = useDetectionStore
          .getState()
          .candidates.filter(
            (c) =>
              c.state === 'ghost' &&
              c.props.score01 < useDetectionStore.getState().threshold,
          );
        for (const g of ghosts) markRejected(g.id);
        addNotification({
          type: 'info',
          title: `${ghosts.length} suggestion${ghosts.length === 1 ? '' : 's'} rejected`,
          duration: 2500,
        });
      },
    });
  };

  const confirmFinishReview = () => {
    const ghostCount = candidates.filter((c) => c.state === 'ghost').length;
    showConfirm({
      title: 'Finish review?',
      message: `Posts your review labels (${acceptedCount} accepted, ${rejectedCount} rejected) and clears the scan${ghostCount ? ` — the remaining ${ghostCount} ghost suggestion${ghostCount === 1 ? '' : 's'} are discarded` : ''}. Accepted indications are kept.`,
      confirmLabel: 'Finish review',
      onConfirm: () => {
        useMapStore.getState().setDetectionPopupCand(null);
        void dismiss();
      },
    });
  };

  // Defense in depth — EditorPage already hides the rail icon + ticket body.
  if (isReadOnly || !canScan) return null;

  // ── Render ──
  const eligibleRows = typeRows.filter((r) => r.selection.eligible);
  const emphasizeAnomaly = eligibleRows.length === 0;

  const scanBlocker = !siteId
    ? 'Link this project to a server site to scan.'
    : !ortho
      ? 'No visible ortho layer — enable one in the Layers panel.'
      : !tilesetId
        ? 'This layer has no HD (COG) tileset yet — generate HD tiles first.'
        : null;

  const similarSection = (
    <div className="detectp__mode">
      <div className="detectp__mode-label">Find similar to my marks</div>
      {typeRows.length === 0 ? (
        <div className="detectp__hint">
          Needs at least {EXEMPLARS_MIN} saved marks of one type on this site.
        </div>
      ) : (
        typeRows.map(({ type, selection, syncedCount }) => (
          <button
            key={type}
            type="button"
            className="detectp__type-item"
            disabled={!scanReady || !selection.eligible}
            title={
              selection.eligible
                ? TIER_HINT[selection.tier]
                : `Needs ${EXEMPLARS_MIN}+ marks on this ortho (${syncedCount} on site)`
            }
            onClick={() => launch('similar', type, selection.ids)}
          >
            <span
              className="detectp__swatch"
              style={{ background: INDICATION_TYPES[type].color }}
            />
            <span className="detectp__type-label">
              {INDICATION_TYPES[type].label}
              {selection.eligible && (
                <span
                  className={
                    selection.warning
                      ? 'detectp__epoch detectp__epoch--warn'
                      : 'detectp__epoch'
                  }
                  title={selection.warning ?? TIER_HINT[selection.tier]}
                >
                  {selection.warning
                    ? '⚠ mixed dates'
                    : selection.captureDay ?? ''}
                </span>
              )}
            </span>
            {selection.eligible ? (
              <span className="detectp__count">{selection.ids.length}</span>
            ) : (
              <span className="detectp__needs">needs {EXEMPLARS_MIN}+</span>
            )}
          </button>
        ))
      )}
    </div>
  );

  const anomalySection = (
    <div className="detectp__mode">
      <button
        type="button"
        className={`detectp__type-item${emphasizeAnomaly ? ' detectp__type-item--primary' : ''}`}
        disabled={!scanReady}
        onClick={() => launch('anomaly')}
      >
        <span className="detectp__type-label">Suggest anomalies</span>
        <span className="detectp__sub">untyped pre-scan</span>
      </button>
    </div>
  );

  const pitchM = typeof summary?.pitch_m === 'number' ? (summary.pitch_m as number) : null;
  const bandAxis = typeof summary?.band_axis === 'string' ? (summary.band_axis as string) : null;

  return (
    <div className="detectp">
      {/* ═══ SCAN ═══ */}
      <div className="detectp__section">
        <div className="detectp__section-title">Scan</div>
        {orthoName && (
          <div className="detectp__ortho" title={orthoName}>
            <span className="detectp__ortho-label">Current layer</span>
            <span className="detectp__ortho-name">{orthoName}</span>
          </div>
        )}
        {scanBlocker && !busy && !failed && (
          <div className="detectp__hint">{scanBlocker}</div>
        )}

        {/* Mode pickers — hidden while a scan is running or errored */}
        {!scanBlocker && !busy && !failed && (
          emphasizeAnomaly ? (
            <>
              {anomalySection}
              {similarSection}
            </>
          ) : (
            <>
              {similarSection}
              {anomalySection}
            </>
          )
        )}

        {/* Job status area */}
        {busy && (
          <div className="detectp__status detectp__status--busy">
            <span className="detectp__spinner" />
            <span>
              Scanning…
              {scanStartedAt != null && (
                <span className="detectp__elapsed"> {formatElapsed(nowTick - scanStartedAt)}</span>
              )}
            </span>
          </div>
        )}
        {failed && (
          <div className="detectp__status detectp__status--error">
            <div className="detectp__error-msg">
              Scan failed: {job?.error || 'Unknown error'}
            </div>
            <div className="detectp__status-actions">
              {lastDispatch && lastDispatch.siteId === siteId && (
                <button type="button" className="detectp__btn" onClick={handleRetry}>
                  Retry
                </button>
              )}
              <button type="button" className="detectp__btn detectp__btn--ghost" onClick={() => clear()}>
                Dismiss
              </button>
            </div>
          </div>
        )}
        {status === 'complete' && (
          <div className="detectp__status detectp__status--done">
            <span className="detectp__chip detectp__chip--accent">
              {job?.candidateCount ?? candidates.length} candidates
            </span>
            {pitchM != null && (
              <span className="detectp__chip" title="Detected module pitch">
                pitch {pitchM.toFixed(2)} m
              </span>
            )}
            {bandAxis && (
              <span className="detectp__chip" title="Diode-substring band axis">
                band {bandAxis}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ═══ REVIEW ═══ */}
      {candidates.length > 0 && (
        <div className="detectp__section">
          <div className="detectp__section-title">Review</div>

          <div className="detectp__slider-row">
            <label className="detectp__slider-label" htmlFor="detectp-threshold">
              Sensitivity
            </label>
            <input
              id="detectp-threshold"
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="detectp__slider"
            />
            <span className="detectp__slider-value">{Math.round(threshold * 100)}%</span>
          </div>
          <div className="detectp__hint">
            {visibleGhosts.length} ghost{visibleGhosts.length === 1 ? '' : 's'} visible on the map
          </div>

          <div
            className="detectp__list"
            role="listbox"
            aria-label="Detection candidates, ranked by score"
            tabIndex={0}
            onKeyDown={handleListKeyDown}
          >
            {ranked.map((cand, idx) => {
              const pct = Math.round(cand.props.score01 * 100);
              const hiddenByThreshold =
                cand.state === 'ghost' && cand.props.score01 < threshold;
              return (
                <button
                  key={cand.id}
                  ref={(el) => { rowRefs.current[idx] = el; }}
                  type="button"
                  role="option"
                  aria-selected={idx === activeIdx}
                  className={
                    'detectp__row' +
                    (idx === activeIdx ? ' detectp__row--active' : '') +
                    (hiddenByThreshold ? ' detectp__row--dim' : '') +
                    (cand.state === 'rejected' ? ' detectp__row--rejected' : '')
                  }
                  onClick={() => {
                    setActiveIdx(idx);
                    focusCandidate(cand);
                  }}
                  title={`Score ${cand.props.score01.toFixed(3)} (${cand.props.mode}) — click to fly to it`}
                >
                  <span className="detectp__row-score">{pct}%</span>
                  <span className="detectp__row-mode">{cand.props.mode}</span>
                  <span className={`detectp__state detectp__state--${cand.state}`}>
                    {cand.state === 'ghost'
                      ? hiddenByThreshold ? 'below' : 'ghost'
                      : cand.state}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="detectp__keys">
            ↑↓ navigate · Enter accept · Backspace/R reject
          </div>
        </div>
      )}

      {/* ═══ BATCH ═══ */}
      {candidates.length > 0 && (
        <div className="detectp__section">
          <div className="detectp__section-title">Batch</div>
          <button
            type="button"
            className="detectp__btn detectp__btn--primary detectp__btn--block"
            disabled={
              job?.scanMode !== 'similar' || !scanType || visibleGhosts.length === 0 || batchBusy
            }
            title={
              job?.scanMode === 'anomaly'
                ? 'anomaly suggestions need a type each'
                : visibleGhosts.length === 0
                  ? 'No visible ghosts at the current sensitivity'
                  : undefined
            }
            onClick={confirmAcceptAll}
          >
            {batchBusy ? 'Creating…' : `Accept all visible (${visibleGhosts.length})`}
          </button>
          <button
            type="button"
            className="detectp__btn detectp__btn--danger detectp__btn--block"
            disabled={belowGhosts.length === 0 || batchBusy}
            title={belowGhosts.length === 0 ? 'No ghosts below the slider' : undefined}
            onClick={confirmRejectBelow}
          >
            Reject all below slider ({belowGhosts.length})
          </button>
        </div>
      )}

      {/* ═══ FOOTER ═══ */}
      {job && !busy && (
        <div className="detectp__footer">
          <div className="detectp__tally">
            <span className="detectp__tally-accepted">{acceptedCount} accepted</span>
            <span className="detectp__tally-sep">·</span>
            <span className="detectp__tally-rejected">{rejectedCount} rejected</span>
          </div>
          <button
            type="button"
            className="detectp__btn detectp__btn--block"
            onClick={confirmFinishReview}
          >
            Finish review
          </button>
        </div>
      )}
    </div>
  );
}
